import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { concat, defer, Observable, of, Subscriber } from 'rxjs';
import { Project } from 'src/app/core/objects/project';
import { ImportFromCsvService } from 'src/app/core/services/import-from-csv.service';
import { ImportFromXlsxService } from 'src/app/core/services/import-from-xlsx.service';
import { ProjectApiService } from 'src/app/core/services/project-api.service';
import { ProjectService } from 'src/app/core/services/project.service';
import { UserApiService } from 'src/app/core/services/user-api.service';
import { WhiteLabelService } from 'src/app/core/services/white-label.service';
import { CompanyBranding, Load, WorkSpaceStop } from 'src/app/core/types';
import { ModalOverlayRef } from '../../modal/modal-overlay-ref';
import { ImportExportService } from '../../core/services/import-export.service';
import { switchMap } from 'rxjs/operators';
import { read, utils, WorkBook, WorkSheet } from 'xlsx';
import {
  CsvColumns,
  LoadingSpaceToAddInterface,
  ProjectData,
} from '../../core/services/import-export-service-types';

@Component({
    selector: 'gl-import-csv',
    templateUrl: './import-csv.component.html',
    styleUrls: ['./import-csv.component.scss'],
    standalone: false
})
export class ImportCsvComponent implements OnInit {
  public errorTranslationName: string;
  public errorTranslationMessage: string;
  public form: UntypedFormGroup;
  public companyBranding: CompanyBranding;
  @ViewChild('fileInput', { static: false, read: ElementRef }) private fileInput: ElementRef;
  private currentWorkSpace: number;
  private project: Project;
  public errorsList$: Observable<string[]>;

  constructor(
    private projectService: ProjectService,
    private importFromCsv: ImportFromCsvService,
    private importFromXlsx: ImportFromXlsxService,
    private projectApiService: ProjectApiService,
    private userApiService: UserApiService,
    private modalRef: ModalOverlayRef,
    private whiteLabelService: WhiteLabelService,
    private importExportService: ImportExportService,
    private translate: TranslateService,
  ) {
    this.projectService.currentWorkSpaceIndex.subscribe(
      (currentWorkSpace: number) => (this.currentWorkSpace = currentWorkSpace),
    );
    this.projectService.project.subscribe((project: Project) => (this.project = project));
    this.companyBranding = this.whiteLabelService.getState();
    this.modalRef.backgroundClicked();
    this.form = this.generateForm();
    this.errorsList$ = this.importExportService.errors$.asObservable();
  }

  public downloadCsvTemplate(): void {
    const link: HTMLAnchorElement = document.createElement('a');
    link.href = `/assets/csv/template_${this.translate.currentLang}.csv`;
    link.download = `${this.translate.instant('loads') as string}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  public downloadXlsxTemplate(): void {
    const link: HTMLAnchorElement = document.createElement('a');
    link.href = `/assets/xlsx/template_${this.translate.currentLang}.xlsx`;
    link.download = `${this.translate.instant('loads') as string}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  public getInstructionLink(): string {
    const currentLang: string = this.translate.currentLang;
    const lang: string = currentLang === 'pl' ? 'pl' : 'en';
    return `/assets/import_export_instructions/instruction_${lang}.pdf`;
    // if (this.companyBranding?.logoUrl) {
    //   return `/assets/pdf/instruction_${this.translate.currentLang}_wl.pdf`;
    // } else {
    //   return `/assets/pdf/instruction_${this.translate.currentLang}.pdf`;
    // }
  }

  public downloadTemplate(kind: string, isCsv = false): void {
    let fileName: string;
    const fileExtension = isCsv ? 'csv' : 'xlsx';
    switch (true) {
      case kind === 'air':
        fileName = `Air_container.${fileExtension}`;
        break;
      case kind === 'sea':
        fileName = `Marine_container.${fileExtension}`;
        break;
      case kind === 'vehicle':
        fileName = `Vehicle.${fileExtension}`;
        break;
      case kind === 'multistops':
        fileName = `Multistops.${fileExtension}`;
        break;
      default:
        fileName = `Vehicle.${fileExtension}`;
        break;
    }
    const link: HTMLAnchorElement = document.createElement('a');
    link.href = `/assets/import_export_templates/${fileName}`;
    link.download = `${fileName}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  public handleFiles(file: File): void {
    const isCsvFile: boolean = this.importExportService.isCsvFile(file);
    if (isCsvFile) {
      const reader: FileReader = new FileReader();
      const readerObservable: Observable<string> = new Observable<string>(
        (observer: Subscriber<string>): void => {
          reader.onload = (): void => {
            observer.next(reader.result as string);
            observer.complete();
          };
          reader.onerror = (error: ProgressEvent<FileReader>) => observer.error(error);
          reader.readAsText(file);
        },
      );

      readerObservable.subscribe((text: string): void => {
        const isFileNew: boolean = this.checkIfDataContainsStrings(text, [
          CsvColumns.CONFIG_UNITS.replace(/_/g, ' '),
          CsvColumns.LOAD_NAME.replace(/_/g, ' '),
        ]);
        if (isFileNew) {
          this.handleFile(file);
        } else {
          this.handleCsvFile(file);
        }
      });
    } else if (this.importExportService.isXlsxFile(file)) {
      const reader: FileReader = new FileReader();
      const readerObservable: Observable<string[][]> = new Observable<string[][]>(
        (observer: Subscriber<string[][]>): void => {
          reader.onload = (e: ProgressEvent<FileReader>): void => {
            const workBook: WorkBook = read(e.target.result);
            const workSheetName: string = workBook.SheetNames[0];
            const workSheet: WorkSheet = workBook.Sheets[workSheetName];
            const data: string[][] = utils.sheet_to_json(workSheet, { header: 1 });
            try {
              observer.next(data);
              observer.complete();
            } catch (error) {
              observer.error(error);
            }
          };
          reader.onerror = (error: ProgressEvent<FileReader>) => observer.error(error);
          reader.readAsArrayBuffer(file);
        },
      );
      readerObservable.subscribe({
        next: (text: string[][]): void => {
          const textSerialized: string = JSON.stringify(text);
          const isFileNew: boolean = this.checkIfDataContainsStrings(textSerialized, [
            CsvColumns.CONFIG_UNITS.replace(/_/g, ' '),
            CsvColumns.LOAD_NAME.replace(/_/g, ' '),
          ]);
          if (isFileNew) {
            this.handleFile(file);
          } else {
            this.handleCsvFile(file);
          }
        },
      });
    } else {
      this.fileInput.nativeElement.value = null;
      this.importExportService.addError('error_wrong_file_type', null, null);
      this.errorTranslationName = 'error_wrong_file_type';
    }
  }

  private checkIfDataContainsStrings(data: string, keys: string[]): boolean {
    return keys.some((key: string): boolean => data.includes(key));
  }

  public handleFile(file: File): void {
    const shouldSave: boolean = this.form.get('saveLoadsAndLoading').value as boolean;
    this.importExportService.parseFile(file).subscribe(
      (result: ProjectData): void => {
        const convertedData: LoadingSpaceToAddInterface[] =
          this.importExportService.convertDataToApi(result);
        if (shouldSave) {
          const preparedData = this.importExportService.prepareUserLoadsAndSpaces(convertedData);
          this.userApiService
            .saveLoads(preparedData.userLoads)
            .pipe(
              switchMap(() =>
                this.userApiService.saveLoadingSpaces(preparedData.userLoadingSpaces),
              ),
              switchMap(() => of(this.importExportService.importProject(result, convertedData))),
            )
            .subscribe((): void => {
              this.close();
            });
        } else {
          this.importExportService.importProject(result, convertedData);
          this.close();
        }
      },
      (error: any): void => {
        this.fileInput.nativeElement.value = null;
        this.errorTranslationName = error?.name;
        this.errorTranslationMessage = error?.message;
      },
    );
  }

  public getFile(event: Event): File {
    return (event.target as HTMLInputElement).files[0];
  }

  public handleCsvFile(file: File): void {
    if (this.isCsvFile(file)) {
      this.importFromCsv.parse(file).subscribe(
        (data: {
          loadingObjects;
          loadData: { loadsObjects; type };
          stops: WorkSpaceStop[];
        }): void => {
          this.sendData(data);
        },
        (error: Error): void => {
          this.fileInput.nativeElement.value = null;
          this.errorTranslationName = error?.name;
          this.errorTranslationMessage = error?.message;
        },
      );
    } else if (this.isXlsxFile(file)) {
      this.importFromXlsx.parseXLSX(file).subscribe(
        (data: {
          loadingObjects;
          loadData: { loadsObjects; type };
          stops: WorkSpaceStop[];
        }): void => {
          this.sendData(data);
        },
        (error: Error): void => {
          this.fileInput.nativeElement.value = null;
          this.errorTranslationName = error?.name;
          this.errorTranslationMessage = error?.message;
        },
      );
    } else {
      this.fileInput.nativeElement.value = null;
      this.errorTranslationName = 'error_wrong_file_type';
    }
  }

  public close(): void {
    this.modalRef.close();
  }

  private sendData(data: {
    loadingObjects;
    loadData: { loadsObjects; type };
    stops: WorkSpaceStop[];
  }): void {
    const saveLoadAndLoading: boolean = this.form.get('saveLoadsAndLoading').value as boolean;
    const currentWorkSpaceId: number = this.project.workSpaces[this.currentWorkSpace]
      ? this.project.workSpaces[this.currentWorkSpace].id
      : undefined;
    if (!_.isEmpty(data.loadingObjects)) {
      const observables = [];
      data.loadingObjects.forEach((loadingSpace): void => {
        observables.push(
          defer(() => {
            if (loadingSpace.type === 3) {
              loadingSpace = {
                ...loadingSpace,
                palletHeight: loadingSpace.height / 10,
                palletMaxHeight: loadingSpace.height,
              };
            }
            return this.projectApiService.addLoadingSpace({
              currentWorkSpaceId: currentWorkSpaceId,
              newLoadingSpaces: {
                quantity: 1,
                loadingSpace,
                stops: data.stops?.length > 0 ? data.stops : undefined,
              },
            });
          }),
        );
      });
      observables.push(
        defer(() => {
          if (this.project.workSpaces[0].stops?.length > 0) {
            data.loadData.loadsObjects.forEach((newLoad) => {
              const stopOrigin = this.project.workSpaces[0].stops.find(
                (stop) => stop.name === newLoad.load.loadingPlaces.origin.name,
              );
              const stopDestination = this.project.workSpaces[0].stops.find(
                (stop) => stop.name === newLoad.load.loadingPlaces.destination.name,
              );
              if (stopOrigin) {
                newLoad.load.loadingPlaces.origin = {
                  id: stopOrigin.id,
                  name: stopOrigin.name,
                };
              }
              if (stopDestination) {
                newLoad.load.loadingPlaces.destination = {
                  id: stopDestination.id,
                  name: stopDestination.name,
                };
              }
            });
          }

          return this.projectApiService.instantAddGroup({
            currentWorkSpaceId: currentWorkSpaceId,
            newLoads: data.loadData.loadsObjects,
          });
        }),
      );

      if (saveLoadAndLoading) {
        const loads = [];
        data.loadData.loadsObjects.forEach((el: { load: Load }) => loads.push(el.load));
        this.userApiService.saveLoads(loads).subscribe();
        this.userApiService.saveLoadingSpaces(data.loadingObjects).subscribe();
      }
      concat(...observables).subscribe(() => {
        this.close();
      });
    } else {
      this.projectApiService
        .instantAddGroup({
          currentWorkSpaceId: currentWorkSpaceId,
          newLoads: data.loadData.loadsObjects,
          prefferedType: data.loadData.type,
        })
        .subscribe(() => {
          if (saveLoadAndLoading) {
            const loads = [];
            data.loadData.loadsObjects.forEach((el: { load: Load }) => loads.push(el.load));
            this.userApiService.saveLoads(loads).subscribe();
          }
          this.close();
        });
    }
  }

  private generateForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      saveLoadsAndLoading: new UntypedFormControl(),
    });
  }

  private isCsvFile(file: File): boolean {
    return file.type === 'text/csv' || file.name.endsWith('.csv');
  }

  private isXlsxFile(file: File): boolean {
    return (
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.name.endsWith('.xlsx')
    );
  }

  ngOnInit(): void {
    this.importExportService.clearErrors();
  }
}
